import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface StartAssessmentRequest {
  repositoryUrl: string;
  branch: string;
  orionId?: string;
  trackedBusinessApplicationId?: number;
}

export interface StartAssessmentResponse {
  jobId: number;
  queuePosition: number;
  status: string;
  message: string;
}

export enum AssessmentJobType {
  FullCodeAssessment = 0,
  LightweightCodeValidation = 1
}

export interface AssessmentJob {
  id: number;
  status: string; // Pending, Running, Completed, Failed
  jobType?: AssessmentJobType | string;
  repositoryUrl: string;
  branch: string;
  orionId: string | null;
  trackedBusinessApplicationId?: number | null;
  totalScore?: number | null;
  result: AiAssessmentResult | null;
  lightweightResult?: LightweightCodeValidationResult | null;
  errorMessage: string | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  requestedByEmail?: string | null;
  totalPromptTokens?: number;
  totalCompletionTokens?: number;
  totalTokens?: number;
  rateLimitedUntil?: string | null;
  /** Number of retry attempts the backend recorded for this job when GitHub delays were detected. */
  rateLimitRetryCount?: number;
}

export interface LightweightCodeValidationResult {
  repositoryUrl?: string | null;
  branch?: string | null;
  codebaseSize: LightweightCodebaseSize;
  technologyStack: string[];
  legacyIndicators: LightweightCodeIndicator[];
  modernIndicators: LightweightCodeIndicator[];
  deploymentCharacteristics: string[];
  pipelineMaturity: string;
  testCoverageIndicators: string[];
  score?: number | null;
  summary?: string | null;
  signals?: LightweightAssessmentSignal[];
  scoreExplanation?: string | null;
  scoreExplanationSummary?: string | null;
  confidence?: string | null;
  cmdbComparison: string;
  recommendedAction: string;
  message?: string | null;
  validatedAt: string;
}

export interface LightweightAssessmentSignal {
  signal: string;
  evidence: string;
  severity: string;
  reason: string;
}

export interface LightweightCodebaseSize {
  totalFiles: number;
  manifestFiles: number;
  projectFiles: number;
  solutionFiles: number;
}

export interface LightweightCodeIndicator {
  signal: string;
  filePath: string;
  severity: string;
  explanation: string;
}

export interface AssessmentJobHistoryPaged {
  jobs: AssessmentJob[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface AiAssessmentResult {
  orionId?: string;
  applicationName?: string;
  repositoryName?: string;
  totalScore: number;
  modernizationValueIndex: number;
  estimatedSavingsUsd: number;
  valueMessage: string;
  settingsVersion?: number;
  settingsVersionName?: string;
  dimensions: AssessmentDimensions;
  categoryScores: CategoryScores;
  summary?: AiAssessmentSummary;
  additionalDetails?: AiAdditionalDetails;
}

export interface AiAssessmentSummary {
  totalScore: number;
  overallSize: string;
  keyReason: string;
  rationale?: string;
  assumptionsAndGaps: string[];
  recommendation?: string;
}

export interface AiAdditionalDetails {
  recommendedModernizationScope: string[];
  assumptionsAndDependencies: string[];
}

export interface AssessmentDimensions {
  cicd: AssessmentDimension;
  auth: AssessmentDimension;
  code: AssessmentDimension;
  dependencies: AssessmentDimension;
  hosting: AssessmentDimension;
  api: AssessmentDimension;
  container: AssessmentDimension;
  testing: AssessmentDimension;
  production: AssessmentDimension;
  database?: AssessmentDimension; // optional — old assessments pre-date this dimension
}

export interface AssessmentDimension {
  dimension: string;
  size: string; // "Small", "Medium", "Large", "N/A"
  score: number;
  reasoning: string; // Bullet list as newline-delimited string ("- item 1\n- item 2") or legacy plain text
  actionItems?: string[]; // Per-dimension modernization action items (may be absent on legacy assessments)
}

export interface CategoryScores {
  operationalEfficiencyScore: number;
  technicalAgilityScore: number;
  cloudOptimizationScore: number;
  securityComplianceScore: number;
}

@Injectable({
  providedIn: 'root'
})
export class AiAssessmentService {
  private apiUrl: string;

  constructor(private http: HttpClient) {
    this.apiUrl = environment.apiUrl + '/assessment';
  }

  /**
   * Start a new AI assessment job
   */
  startAssessment(request: StartAssessmentRequest): Observable<StartAssessmentResponse> {
    return this.http.post<StartAssessmentResponse>(`${this.apiUrl}/start`, request);
  }

  /**
   * Start a lightweight metadata-only code validation job.
   */
  startLightweightValidation(request: StartAssessmentRequest): Observable<StartAssessmentResponse> {
    return this.http.post<StartAssessmentResponse>(`${this.apiUrl}/start-lightweight-validation`, request);
  }

  /**
   * Get assessment job by ID
   */
  getJob(jobId: number): Observable<AssessmentJob> {
    return this.http.get<AssessmentJob>(`${this.apiUrl}/job/${jobId}`);
  }

  /**
   * Get assessment history for an Orion ID
   */
  getJobHistory(orionId: string): Observable<AssessmentJob[]> {
    return this.http.get<AssessmentJob[]>(`${this.apiUrl}/history/${orionId}`);
  }

  /**
   * Get assessment history for a TrackedBusinessApplicationId
   * @param includeResults Whether to include full result JSON (default: false for better performance)
   */
  getJobHistoryByTrackedBusinessApplicationId(trackedBusinessApplicationId: number, includeResults: boolean = false): Observable<AssessmentJob[]> {
    return this.http.get<AssessmentJob[]>(`${this.apiUrl}/history/by-app/${trackedBusinessApplicationId}`, {
      params: { includeResults: includeResults.toString() }
    });
  }

  /**
   * Get the fair-order queue position for a job
   */
  getQueuePosition(jobId: number): Observable<{ jobId: number; queuePosition: number }> {
    return this.http.get<{ jobId: number; queuePosition: number }>(`${this.apiUrl}/queue-position/${jobId}`);
  }

  /**
   * Get all assessment jobs with pagination
   */
  getAllJobsPaged(page: number = 1, pageSize: number = 20): Observable<AssessmentJobHistoryPaged> {
    return this.http.get<AssessmentJobHistoryPaged>(`${this.apiUrl}/history?page=${page}&pageSize=${pageSize}`);
  }
}
